import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  EventMapping,
  MessageLogEntry,
  MessageLogPage,
  MessagePreview,
  MessageTemplateList,
  MessagingConnection,
  MessagingSettings,
  NotificationEvent,
  OrderMessagePreview,
  OrderMessages,
  TestSendInput,
  UpdateEventMappingInput,
  UpdateMessagingConnectionInput,
  UpdateMessagingSettingsInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  disconnectWorkspace,
  getMessagingSettings,
  getOrderMessages,
  listMessages,
  listTemplates,
  previewEvent,
  previewOrderMessage,
  retryMessage,
  saveConnection,
  saveEventMapping,
  saveMessagingPreferences,
  sendOrderMessage,
  sendTestMessage,
  testConnection,
  type MessageLogParams,
} from './notifications-api.js';

/**
 * The messaging screen's data layer.
 *
 * Two habits are worth naming. Anything that touches the connection invalidates the TEMPLATES
 * as well as the settings — a new key is very often a different workspace, and a dropdown
 * still offering the old shop's templates is how a mapping ends up pointing at nothing. And a
 * saved mapping is written straight back into the settings cache rather than refetched, so the
 * row the owner just edited does not flicker back to its old state for a moment.
 */

export const notificationKeys = {
  all: ['notifications'] as const,
  settings: () => ['notifications', 'settings'] as const,
  templates: () => ['notifications', 'templates'] as const,
  preview: (event: NotificationEvent) => ['notifications', 'preview', event] as const,
  log: (params: MessageLogParams) => ['notifications', 'log', params] as const,
};

export function useMessagingSettings(): UseQueryResult<MessagingSettings> {
  const client = useApiClient();
  return useQuery({
    queryKey: notificationKeys.settings(),
    queryFn: () => getMessagingSettings(client),
  });
}

export function useMessageTemplates(options: { enabled?: boolean } = {}) {
  const client = useApiClient();
  return useQuery<MessageTemplateList>({
    queryKey: notificationKeys.templates(),
    queryFn: () => listTemplates(client),
    enabled: options.enabled ?? true,
  });
}

/** Refetch the settings AND the templates: a credential change can change both. */
function useConnectionInvalidation(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };
}

export function useSaveConnection() {
  const client = useApiClient();
  const invalidate = useConnectionInvalidation();
  return useMutation<MessagingConnection, unknown, UpdateMessagingConnectionInput>({
    mutationFn: (input) => saveConnection(client, input),
    onSuccess: invalidate,
  });
}

export function useTestConnection() {
  const client = useApiClient();
  const invalidate = useConnectionInvalidation();
  return useMutation<MessagingConnection, unknown, void>({
    mutationFn: () => testConnection(client),
    onSuccess: invalidate,
  });
}

export function useDisconnectWorkspace() {
  const client = useApiClient();
  const invalidate = useConnectionInvalidation();
  return useMutation<MessagingConnection, unknown, void>({
    mutationFn: () => disconnectWorkspace(client),
    onSuccess: invalidate,
  });
}

export function useSaveMessagingPreferences() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MessagingSettings, unknown, UpdateMessagingSettingsInput>({
    mutationFn: (patch) => saveMessagingPreferences(client, patch),
    onSuccess: (settings) => {
      queryClient.setQueryData(notificationKeys.settings(), settings);
    },
  });
}

export function useSaveEventMapping() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<
    EventMapping,
    unknown,
    { event: NotificationEvent; patch: UpdateEventMappingInput }
  >({
    mutationFn: ({ event, patch }) => saveEventMapping(client, event, patch),
    onSuccess: (mapping) => {
      queryClient.setQueryData<MessagingSettings>(
        notificationKeys.settings(),
        (previous) =>
          previous && {
            ...previous,
            events: previous.events.map((row) => (row.event === mapping.event ? mapping : row)),
          },
      );
      // The preview is rendered from the mapping that just changed.
      void queryClient.invalidateQueries({ queryKey: notificationKeys.preview(mapping.event) });
    },
  });
}

export function useEventPreview(event: NotificationEvent | null): UseQueryResult<MessagePreview> {
  const client = useApiClient();
  return useQuery({
    queryKey: notificationKeys.preview(event ?? 'order_ready'),
    queryFn: () => previewEvent(client, event as NotificationEvent),
    enabled: event !== null,
  });
}

export function useSendTestMessage() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MessageLogEntry, unknown, TestSendInput>({
    mutationFn: (input) => sendTestMessage(client, input),
    onSuccess: () => {
      // A test send appears in the log like any other message.
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'log'] });
    },
  });
}

export function useMessageLog(params: MessageLogParams = {}): UseQueryResult<MessageLogPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: notificationKeys.log(params),
    queryFn: () => listMessages(client, params),
  });
}

export function useRetryMessage() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MessageLogEntry, unknown, string>({
    mutationFn: (messageId) => retryMessage(client, messageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'log'] });
    },
  });
}

// --- One order's messages (task 15) ---------------------------------------------------------

export const orderMessageKeys = {
  all: (orderId: string) => ['orders', orderId, 'messages'] as const,
  preview: (orderId: string, event: NotificationEvent) =>
    ['orders', orderId, 'messages', 'preview', event] as const,
};

export function useOrderMessages(orderId: string | null): UseQueryResult<OrderMessages> {
  const client = useApiClient();
  return useQuery({
    queryKey: orderMessageKeys.all(orderId ?? ''),
    queryFn: () => getOrderMessages(client, orderId ?? ''),
    enabled: Boolean(orderId),
  });
}

/**
 * The preview behind the send button. Fetched only while the dialog is open, and never held:
 * it quotes the customer's balance, and a stale balance in a message somebody is about to send
 * is worse than a moment's spinner.
 */
export function useOrderMessagePreview(
  orderId: string,
  event: NotificationEvent | null,
): UseQueryResult<OrderMessagePreview> {
  const client = useApiClient();
  return useQuery({
    queryKey: orderMessageKeys.preview(orderId, event ?? 'order_ready'),
    queryFn: () => previewOrderMessage(client, orderId, event as NotificationEvent),
    enabled: Boolean(event),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSendOrderMessage(orderId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MessageLogEntry, unknown, NotificationEvent>({
    mutationFn: (event) => sendOrderMessage(client, orderId, event),
    onSuccess: () => {
      // The timeline is the evidence the shop has just added to. Refetch rather than patch, so
      // the delivery status the webhook fills in a moment later is picked up with it.
      void queryClient.invalidateQueries({ queryKey: orderMessageKeys.all(orderId) });
    },
  });
}
